<template>
  <div class="fill-height">
    <v-card flat color="#f5f5f5" width="100%" class="mx-auto">
      <v-data-table
        :headers="auditHeaders"
        :items="audits"
        :footer-props="{ 'items-per-page-options': [100] }"
        :items-per-page="100"
        :loading="loading"
        class="elevation-1 py-2"
      >
        <template v-slot:no-data>
          <div v-if="loading" class="text-center py-4">Loading audits...</div>
          <div v-else class="text-center py-4">No data available.</div>
        </template>

        <template v-slot:item.title="{ item }">
          <div class="subtitle-1 pt-3 font-weight-bold">{{ item.title }}</div>
          <div v-html="highlightKeywords(item.matched_keywords)" class="py-3 caption font-italic"></div>
          <div class="pb-5 pt-0">
            <span v-for="tag in consolidateTags(item)" :key="tag.id">
              <v-chip class="my-1 ml-0 mr-2" color="primary" label text-color="secondary" small outlined>
                <v-icon icon="mdi-label" small left></v-icon>
                {{ tag.name }}
              </v-chip>
            </span>
          </div>
        </template>

        <template v-slot:item.published_date="{ item }">
          {{ formatDate(item.published_date) }}
        </template>

        <template v-slot:item.gov_type="{ item }">
          <v-avatar :color="govAvatarColor(item.gov_type)" size="36" class="mr-2">
            <span class="white--text caption">{{ item.gov_type }}</span>
          </v-avatar>
        </template>

        <template v-slot:item.description="{ item }">
          {{ item.description }}
        </template>

        <template v-slot:item.entities="{ item }">
          <div>
            <p v-for="entity in item.entities" :key="entity.id">{{ entity.name }}</p>
          </div>
        </template>

        <template v-slot:item.findings="{ item }">
          <v-btn
            large icon color="secondary"
            v-if="item.findings"
            text
            @click="selectedAudit = item.title; selectedFindings = item.findings; showFinding = !showFinding"
          >
            <v-icon>mdi-eye-circle-outline</v-icon>
          </v-btn>
        </template>

        <template v-slot:item.conclusion="{ item }">
          <div class="text-body-2 my-4 text-grey-lighten-2">{{ item.conclusion }}</div>
        </template>

        <template v-slot:item.page_url="{ item }">
          <v-tooltip top dark color="#000000" style="opacity: 0.7;">
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" large icon light :href="item.page_url" target="_blank">
                <v-icon>mdi-web</v-icon>
              </v-btn>
            </template>
            <span>Go to Source</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog width="80%" v-model="showFinding">
      <v-card tile flat class="pa-3">
        <v-card-title>
          <span>
            <v-card-subtitle class="pa-0 font-weight-light">Summary of Findings</v-card-subtitle>
            <span>{{ selectedAudit }}</span>
          </span>
          <div class="flex-grow-1"></div>
          <v-btn icon @click="showFinding = false">
            <v-icon medium>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-simple-table class="pa-3">
          <thead>
            <tr>
              <th class="text-left text-subtitle-1 pt-5 font-weight-black">Findings</th>
              <th class="text-left text-subtitle-1 pt-5 font-weight-black">Recommendations</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="finding in selectedFindings" :key="finding.id">
              <td class="text-body-2 py-5 pr-5">{{ finding.detail }}</td>
              <td class="text-body-2 py-5 pl-5">
                <ul>
                  <li v-for="recommendation in finding.recommendations" :key="recommendation.id">
                    {{ recommendation.text }}
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { advancedFilterAudits, getAudits } from "./APIManager.js";
import { govColor } from "./Utils.js";

export default {
  data: () => ({
    audits: [],
    showFinding: false,
    selectedAudit: "",
    selectedFindings: [],
    auditHeaders: [
      { text: 'Jurisdiction', value: 'gov_type', align: 'center' },
      { text: 'Audit Title', value: 'title' },
      { text: 'Published', value: 'published_date' },
      { text: 'Summary', value: 'description' },
      { text: 'Related Entities', value: 'entities' },
      { text: 'Findings', value: 'findings', align: 'center', sortable: false },
      { text: 'Conclusion', value: 'conclusion' },
      { text: 'Report', value: 'page_url', align: 'center', sortable: false }
    ],
    panel: null,
    error: false,
    loading: false,
    openPanel: false,
    searchTerm: '',
    searchChips: [],
    searchObject: {},
  }),

  mounted() {
    this.loadAudits();
  },

  watch: {
    $route() {
      this.loadAudits();
    },
  },

  methods: {
    loadAudits() {
      const query = this.$route.query;
      this.searchChips = [];
      this.searchObject = {};

      const arrayProps = ["tender", "contact", "supplier", "publisher", "category", "major_category", "government", "publisher_industry", "supplier_industry"];
      const dateProps = ["published_from", "published_to"];

      arrayProps.forEach(prop => {
        if (query[prop] && Array.isArray(query[prop])) {
          this.searchChips.push(...query[prop]);
          this.searchObject[prop] = query[prop];
        }
      });

      dateProps.forEach(prop => {
        if (query[prop]) {
          this.searchChips.push(query[prop]);
          this.searchObject[prop] = query[prop];
        }
      });

      this.searchChips.length ? this.searchAudits() : this.fetchAudits();
    },

    async fetchAudits() {
      this.loading = true;
      try {
        const response = await getAudits();
        this.audits = response.data.map(item => ({
          ...item.audit,
          matched_keywords: item.matched_keywords
        }));
      } catch {
        this.error = true;
      } finally {
        this.loading = false;
        this.$emit('fetchComplete', text);
      }
    },

    async searchAudits() {
      this.loading = true;
      try {
        const response = await advancedFilterAudits(this.searchObject);
        this.audits = response.data.map(item => ({
          ...item.audit,
          matched_keywords: item.matched_keywords
        }));
      } catch {
        this.error = true;
      } finally {
        const text = this.audits.length
          ? `There are ${this.audits.length} Audit reports matching the search.`
          : "There are no Audit reports matching the search.";
        this.$emit('fetchComplete', text);
        this.loading = false;
      }
    },

    formatDate(inputDate) {
      const date = new Date(inputDate);
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
        'August', 'September', 'October', 'November', 'December'];
      return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    },

    consolidateTags(audit) {
      const tags = audit.tags || [];
      const activity = audit.activity_tags ? audit.activity_tags.split(',') : []; 
      const sector = audit.sector_tags ? audit.sector_tags.split(',') : [];
      return [...tags, ...activity, ...sector].map((tag, i) =>
        typeof tag === 'object' ? tag : { id: i, name: tag.trim() }
      );
    },

    highlightKeywords(matchedKeywords) {
      return matchedKeywords.map(group =>
        group.map(k =>
          k.replace(/<em>/g, '<span style="background-color: yellow; color:black">')
            .replace(/<\/em>/g, '</span>')
        ).join(' ')
      ).join(' ');
    },

    govAvatarColor(gov) {
      return govColor(gov);
    }
  }
};
</script>

<style scoped>
.title {
  font-size: 16px;
  font-weight: bold;
}
.subtitle {
  font-size: 14px;
  color: grey;
}
.highlight {
  background-color: yellow;
  font-style: normal;
}
</style>
